import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { visibleGroups } from "./nav-items";

const COLLAPSED_KEY = "md_sidebar_collapsed";

interface SidebarProps {
  variant?: "desktop" | "mobile";
  onItemClick?: () => void;
}

export function Sidebar({ variant = "desktop", onItemClick }: SidebarProps) {
  const { isSuperAdmin, canAccess } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (variant === "mobile") return;
    const v = localStorage.getItem(COLLAPSED_KEY);
    if (v === "1") setCollapsed(true);
  }, [variant]);

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  }

  const showText = variant === "mobile" || !collapsed;

  const shownGroups = visibleGroups(isSuperAdmin, canAccess);

  const inner = (
    <>
      <div
        className="flex h-16 shrink-0 items-center gap-3 border-b border-primary/10 px-4"
        style={variant === "mobile" ? { paddingTop: "env(safe-area-inset-top)", height: "calc(4rem + env(safe-area-inset-top))" } : undefined}
      >
        <Logo size={36} />
        <AnimatePresence initial={false}>
          {showText && (
            <motion.div
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -4 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col overflow-hidden leading-tight"
            >
              <span className="font-display text-sm font-bold tracking-[0.16em] text-gold">
                MACHIAVELLI
              </span>
              <span className="font-serif text-[10px] italic text-muted-foreground">
                Desk
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <nav className="scroll-touch flex flex-1 flex-col gap-4 overflow-y-auto p-3">
        {shownGroups.map((group, gi) => (
          <div key={group.label} className="flex flex-col gap-0.5">
            <AnimatePresence initial={false}>
              {showText ? (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-2 overflow-hidden px-2 pb-1 pt-1"
                >
                  <span className="font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-primary/50">
                    {group.label}
                  </span>
                  <span
                    aria-hidden
                    className="h-px min-w-2 flex-1 bg-gradient-to-r from-primary/25 to-transparent"
                  />
                </motion.div>
              ) : (
                gi > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    aria-hidden
                    className="mx-auto mb-1 h-px w-6 bg-primary/20"
                  />
                )
              )}
            </AnimatePresence>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onItemClick}
                className={({ isActive }) =>
                  cn(
                    "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200",
                    isActive
                      ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent text-primary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.2)]"
                      : "text-muted-foreground hover:bg-primary/5 hover:text-foreground",
                    // Nel cassetto mobile la riga deve reggere il pollice (44px+)
                    variant === "mobile" && "min-h-[46px] py-3 active:bg-primary/10",
                    collapsed && variant === "desktop" && "justify-center px-2",
                  )
                }
                title={collapsed && variant === "desktop" ? item.label : undefined}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId={`sidebar-active-${variant}`}
                        className="absolute inset-y-1 left-0 w-1 rounded-r-full bg-gradient-to-b from-gold-light via-primary to-gold-muted shadow-glow-primary"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                    <item.icon
                      className={cn(
                        "h-4 w-4 shrink-0 transition-transform group-hover:scale-110",
                        isActive
                          ? "text-gold drop-shadow-[0_0_6px_hsl(var(--primary)/0.6)]"
                          : "group-hover:text-gold-soft",
                      )}
                    />
                    <AnimatePresence initial={false}>
                      {showText && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.15 }}
                          className="overflow-hidden whitespace-nowrap font-medium"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div
        className={cn("shrink-0", variant === "desktop" && "border-t border-white/10 p-3")}
        style={variant === "mobile" ? { height: "env(safe-area-inset-bottom)" } : undefined}
      >
        {variant === "desktop" && (
          <button
            onClick={toggleCollapsed}
            className="flex w-full items-center justify-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-primary/5 hover:text-foreground"
            aria-label={collapsed ? "Espandi sidebar" : "Comprimi sidebar"}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronsLeft className="h-4 w-4" />
                Comprimi
              </>
            )}
          </button>
        )}
      </div>
    </>
  );

  if (variant === "mobile") {
    return <div className="flex h-full flex-col bg-sidebar">{inner}</div>;
  }

  return (
    <motion.aside
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      className="hidden shrink-0 flex-col border-r border-white/10 bg-sidebar md:flex"
    >
      {inner}
    </motion.aside>
  );
}
